import React, { useState } from 'react';
import { withTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import axios from 'axios';
import { useUserStore } from 'store';
import { getCsrfToken, formatQuantity, formatQuantityChange } from '../../utils';
import DecimalField from '../shared/DecimalField';

function StockCountDialog({ t, open, onClose, inventoryItem, onSuccess }) {
  const { currentOrganization } = useUserStore();
  const [countedQuantity, setCountedQuantity] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleClose = () => {
    setCountedQuantity('');
    setNotes('');
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (countedQuantity === '' || parseFloat(countedQuantity) < 0) {
      setError(t('Please enter a valid quantity'));
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await axios.post(
        `/api/inventory/${inventoryItem.id}/set_from_count/?organization=${currentOrganization.organization_id}`,
        {
          counted_quantity: countedQuantity,
          notes: notes,
        },
        {
          headers: {
            'X-CSRFToken': getCsrfToken(),
          },
        }
      );

      onSuccess?.();
      handleClose();
    } catch (err) {
      console.error('Failed to record count:', err);
      setError(err.response?.data?.error || t('Failed to record count'));
    } finally {
      setLoading(false);
    }
  };

  if (!inventoryItem) return null;

  const systemQuantity = parseFloat(inventoryItem.quantity_in_stock) || 0;
  const counted = parseFloat(countedQuantity);
  const difference = !isNaN(counted) ? counted - systemQuantity : null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth data-testid="stock-count-dialog">
      <DialogTitle>{t('inventory.physicalCount')}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {inventoryItem.part_name}
          </Typography>

          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('inventory.systemQuantity')}: <strong>{formatQuantity(inventoryItem.quantity_in_stock)}</strong>
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <DecimalField
            label={t('inventory.countedQuantity')}
            value={countedQuantity}
            onChange={setCountedQuantity}
            required
            autoFocus
            fullWidth
            sx={{ mb: 2 }}
          />

          {difference !== null && (
            <Alert
              severity={difference === 0 ? 'success' : difference > 0 ? 'info' : 'warning'}
              sx={{ mb: 2 }}
            >
              {t('inventory.difference')}: <strong>{formatQuantityChange(difference)}</strong>
              {difference === 0 && ` (${t('Matches system')})`}
            </Alert>
          )}

          <TextField
            label={t('inventory.notes')}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            fullWidth
            size="small"
            multiline
            rows={2}
            helperText={t('Optional - e.g., quarterly inventory')}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {t('Cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || countedQuantity === ''}
          data-testid="stock-count-submit"
        >
          {loading ? <CircularProgress size={20} /> : t('inventory.countRecorded')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default withTranslation()(StockCountDialog);
